import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { authApi } from '@/lib/api'
import { clearPostLoginReturn } from '@/lib/postLogin'
import type { TenantBootstrapResponse } from '@/lib/api'

export interface User {
  id: string
  email: string
  full_name: string
  role: string
  role_level?: number
  holding_id: string | null
  empresa_id?: string
  avatar_url?: string
  language: string
  mfa_enabled: boolean
}

// INC-031: Multi-holding support
export interface HoldingInfo {
  id: string
  code: string
  name: string
  is_default: boolean
}

// SSO Provider info
export interface SSOProviderInfo {
  id: string
  name: string
  display_name: string
  icon: string
  color: string
  enabled: boolean
}

// Proveedores de SSO de un arranque de tenant (por subdominio o, AUTH_04 #298, por correo)
export function ssoProvidersFromBootstrap(data: TenantBootstrapResponse): SSOProviderInfo[] {
  return data.authPolicy.availableSSOProviders.map((p) => ({
    id: p.providerType,
    name: p.displayName,
    display_name: p.displayName,
    icon: p.icon || 'key',
    color: '#6b7280',
    enabled: true,
  }))
}

// AUTH_04 (#298): lo que el dominio del correo dijo sobre el holding, en la direccion comun
export interface LoginDiscovery {
  email: string
  holdingId: string | null
  providers: SSOProviderInfo[]
  allowPassword: boolean
  // "¿Cuenta de emergencia?": sin ir al proveedor, directo a la contrasena
  emergency: boolean
}

// RBAC: Role info with scope
export interface RoleInfo {
  code: string
  name: string
  scope_type: string
}

// ALTA-014: Tenant Bootstrap data
export interface TenantBootstrap {
  tenantSlug: string | null
  holdingId: string | null
  holdingName: string | null
  branding: {
    logoUrl: string | null
    primaryColor: string
    secondaryColor: string
    faviconUrl: string | null
  }
  authPolicy: {
    allowPasswordLogin: boolean
    mfaRequired: boolean
    defaultMethod: string
    availableSSOProviders: Array<{
      providerType: string
      displayName: string
      icon: string | null
    }>
  }
  error: string | null
}

// Map role_level to role string
const ROLE_MAP: Record<number, string> = {
  1: 'gam_admin',
  2: 'admin_cp',
  3: 'admin_holding',
  4: 'admin_empresa',
  5: 'admin_area',
  6: 'usuario_avanzado',
  7: 'usuario_basico',
}

function mapUserFromBackend(backendUser: Record<string, unknown> | null | undefined): User | null {
  if (!backendUser) return null
  const roleLevel = (backendUser.role_level as number) || 7
  // Handle both login response (id) and /me response (user_id)
  const id = (backendUser.id || backendUser.user_id) as string
  // Handle both login response (first_name/last_name) and /me response (email only)
  const firstName = backendUser.first_name as string || ''
  const lastName = backendUser.last_name as string || ''
  const fullName = `${firstName} ${lastName}`.trim() || backendUser.email as string
  // Idioma: cuenta -> eleccion previa guardada -> idioma del navegador -> 'es'.
  // El ultimo eslabon era 'es' HARDCODEADO, y como el registro de usuario puede no
  // traer `language`, un usuario que trabaja en ingles y nunca toco el selector
  // recibia toda la UI y —peor— los DOCUMENTOS GENERADOS en espanol: el modal de
  // Fiori deriva "Idioma del Documento" de `i18n.language`
  // (FioriMigrationPage.tsx:232 y :631), asi que el default se propaga al entregable.
  // Ahora, sin preferencia explicita, se respeta el navegador si su idioma esta
  // entre los 6 soportados.
  const IDIOMAS_SOPORTADOS = ['es', 'en', 'pt', 'fr', 'de', 'it']
  const storedLang = typeof localStorage !== 'undefined' ? localStorage.getItem('i18nextLng') : null
  const navLang =
    typeof navigator !== 'undefined' && navigator.language
      ? navigator.language.slice(0, 2).toLowerCase()
      : null
  const language =
    (backendUser.language as string) ||
    storedLang ||
    (navLang && IDIOMAS_SOPORTADOS.includes(navLang) ? navLang : null) ||
    'es'
  return {
    id,
    email: backendUser.email as string,
    full_name: fullName,
    role: ROLE_MAP[roleLevel] || 'usuario_basico',
    role_level: roleLevel,
    holding_id: (backendUser.holding_id as string) ?? null,
    empresa_id: backendUser.empresa_id as string | undefined,
    language,
    mfa_enabled: (backendUser.mfa_enabled as boolean) || false,
  }
}

interface LoginResult {
  requiresMFA: boolean
  // AUTH_06f (#321): un holding exige 2FA y la cuenta no lo tiene: primero el alta
  requiresMFAEnrollment?: boolean
  requiresHoldingSelection: boolean
  tempToken?: string
  holdings?: HoldingInfo[]
}

// AUTH_01 (#295): el backend rechaza la contrasena de una cuenta enlazada a un
// proveedor externo y devuelve el enlace propio de su holding (puente hasta #298).
// Vive en el store y no en la pagina: mientras isLoading, la ruta publica desmonta
// LoginPage y cualquier estado local se pierde.
export const SSO_ACCOUNT_ERROR_CODE = 'password_login_sso_account'

export interface SSOAccountHint {
  provider: string
  loginUrls: string[]
}

function readSSOAccountHint(error: unknown): SSOAccountHint | null {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data
    ?.detail as { code?: string; provider?: string; login_urls?: string[] } | undefined
  if (!detail || typeof detail !== 'object' || detail.code !== SSO_ACCOUNT_ERROR_CODE) return null
  return { provider: detail.provider ?? '', loginUrls: detail.login_urls ?? [] }
}

// AUTH_06b (#317): errores del ingreso con 2FA. El backend manda solo el codigo
// (I18N_01, #333); la pantalla lo traduce. 'unknown' = cualquier otra falla.
// AUTH_06c (#318): + los de Ajustes (activar, desactivar, codigos nuevos).
// AUTH_06f (#321): + cambiar a un holding que exige 2FA sin tenerlo.
export type MFAErrorCode =
  | 'mfa_code_invalid'
  | 'mfa_token_invalid'
  | 'mfa_locked'
  | 'mfa_unavailable'
  | 'mfa_not_configured'
  | 'mfa_policy_missing'
  | 'mfa_password_invalid'
  | 'mfa_password_not_set'
  | 'mfa_already_enabled'
  | 'mfa_not_enabled'
  | 'mfa_setup_not_started'
  | 'mfa_setup_changed'
  | 'mfa_enrollment_required'
  | 'unknown'

export interface MFAError {
  code: MFAErrorCode
  retryAfterSeconds?: number
}

const MFA_ERROR_CODES: MFAErrorCode[] = [
  'mfa_code_invalid',
  'mfa_token_invalid',
  'mfa_locked',
  'mfa_unavailable',
  'mfa_not_configured',
  'mfa_policy_missing',
  'mfa_password_invalid',
  'mfa_password_not_set',
  'mfa_already_enabled',
  'mfa_not_enabled',
  'mfa_setup_not_started',
  'mfa_setup_changed',
  'mfa_enrollment_required',
]

export function readMFAError(error: unknown): MFAError | null {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data
    ?.detail as { code?: string; retry_after_seconds?: unknown } | undefined
  if (!detail || typeof detail !== 'object') return null
  const code = MFA_ERROR_CODES.find((c) => c === detail.code)
  if (!code) return null
  const retry = detail.retry_after_seconds
  return { code, retryAfterSeconds: typeof retry === 'number' ? retry : undefined }
}

// AUTH_03 (#297): errores del inicio y del retorno del SSO. El backend manda solo el
// codigo (I18N_01, #333); la pantalla lo traduce. 'sso_state_mismatch' y
// 'sso_missing_params' los detecta el navegador. 'unknown' = cualquier otra falla.
// AUTH_11 (#309): + 'no_holding_assigned', que llega igual por la contrasena y por el SSO.
export const NO_HOLDING_ERROR_CODE = 'no_holding_assigned'
// AUTH_24 (#359): cuenta desactivada o suspendida, el mismo codigo por las dos puertas
export const ACCOUNT_INACTIVE_ERROR_CODE = 'account_inactive'

export type SSOErrorCode =
  | 'sso_state_used'
  | 'sso_state_invalid'
  | 'sso_state_mismatch'
  | 'sso_missing_params'
  | 'sso_unavailable'
  | 'sso_user_not_provisioned'
  | 'sso_provider_not_configured'
  | 'password_login_holding_sso'
  | 'sso_redirect_host_invalid'
  | typeof NO_HOLDING_ERROR_CODE
  | typeof ACCOUNT_INACTIVE_ERROR_CODE
  | 'unknown'

const SSO_SERVER_ERROR_CODES: SSOErrorCode[] = [
  'sso_state_used',
  'sso_state_invalid',
  'sso_unavailable',
  'sso_user_not_provisioned',
  // AUTH_04 (#298): sin configuracion global, el holding sin su proveedor no arranca el SSO
  'sso_provider_not_configured',
  // AUTH_04 (#298): contrasena en un holding solo-SSO sin ser cuenta de emergencia
  'password_login_holding_sso',
  // #581: return_host fuera de la lista; el inicio del SSO lo rechaza en el gateway
  'sso_redirect_host_invalid',
  NO_HOLDING_ERROR_CODE,
  ACCOUNT_INACTIVE_ERROR_CODE,
]

// #581: hosts a los que el retorno del SSO puede redirigir con los tokens. La misma lista
// valida el gateway al iniciar el SSO (backend/api_gateway/src/app/sso_return_host.py).
// Produccion: app.aldo.bupry.com y <slug>.app.aldo.bupry.com (slug [a-z0-9-]{2,63}, sin
// puerto). localhost solo en desarrollo.
const SSO_REDIRECT_HOSTS = /^([a-z0-9-]{2,63}\.)?app\.aldo\.bupry\.com$/
const SSO_REDIRECT_HOSTS_DEV = /^localhost(:\d{1,5})?$/

export function isAllowedSSORedirectHost(host: string, dev: boolean = import.meta.env.DEV): boolean {
  return SSO_REDIRECT_HOSTS.test(host) || (dev && SSO_REDIRECT_HOSTS_DEV.test(host))
}

export class SSOError extends Error {
  readonly code: SSOErrorCode

  constructor(code: SSOErrorCode) {
    super(code)
    this.code = code
  }
}

export function readSSOError(error: unknown): SSOErrorCode {
  if (error instanceof SSOError) return error.code
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data
    ?.detail as { code?: string } | undefined
  if (!detail || typeof detail !== 'object') return 'unknown'
  return SSO_SERVER_ERROR_CODES.find((c) => c === detail.code) ?? 'unknown'
}

// AUTH_03 (#297): el retorno del SSO se envia una sola vez por identificador. La pagina
// puede pedirlo de nuevo (React en desarrollo, el cambio de idioma al abrir la sesion) y
// el segundo envio fallaba porque el servidor ya lo habia gastado. Quien lo pide otra vez
// recibe el mismo resultado. Fuera del store: sobrevive a que la pagina se monte de nuevo.
const ssoCallbacks = new Map<string, Promise<LoginResult>>()

interface LoginResponseData {
  access_token: string
  user?: Record<string, unknown> & { empresa_id?: string }
  holdings?: HoldingInfo[]
  requires_holding_selection?: boolean
  temp_token?: string
}

// AUTH_06f (#321): alta obligatoria del 2FA. Vive en memoria, no se guarda en el navegador.
export interface MFAEnrollment {
  token: string // pase de alta de /auth/login: solo sirve para /auth/mfa/enroll/*
  email: string // para el archivo de los codigos de respaldo
  // el ingreso ya confirmado; se aplica cuando la persona dice que guardo sus codigos
  pendingLogin: LoginResponseData | null
}

// Pase de alta vencido o gastado: solo queda volver a la contrasena. Con un codigo
// malo o una falla pasajera sigue vivo.
function stateAfterEnrollmentError(error: unknown, enrollment: MFAEnrollment): Partial<AuthState> {
  const mfaError = readMFAError(error) ?? { code: 'unknown' as const }
  return { mfaError, mfaEnrollment: mfaError.code === 'mfa_token_invalid' ? null : enrollment }
}

// Lo que sigue a un ingreso ya resuelto (contrasena y, si la cuenta lo tiene, 2FA):
// elegir holding o abrir la sesion. AUTH_06b (#317): lo comparten login y
// completeMFALogin, asi el 2FA termina igual que un ingreso normal.
function stateAfterLogin(data: LoginResponseData): { state: Partial<AuthState>; result: LoginResult } {
  // INC-031: Handle multi-holding selection
  const holdings = (data.holdings || []) as HoldingInfo[]

  if (data.requires_holding_selection) {
    return {
      state: {
        isLoading: false,
        holdings,
        requiresHoldingSelection: true,
        tempToken: data.temp_token ?? null,
      },
      result: { requiresMFA: false, requiresHoldingSelection: true, tempToken: data.temp_token, holdings },
    }
  }

  // Normal login with automatic holding selection
  localStorage.setItem('token', data.access_token)
  const activeHoldingId = holdings.length > 0 ? holdings[0].id : null
  if (activeHoldingId) {
    localStorage.setItem('holdingId', activeHoldingId)
  }
  if (data.user?.empresa_id) {
    localStorage.setItem('empresaId', data.user.empresa_id)
  }

  return {
    state: {
      user: mapUserFromBackend(data.user),
      token: data.access_token,
      isAuthenticated: true,
      isLoading: false,
      holdings,
      activeHoldingId,
      requiresHoldingSelection: false,
      tempToken: null,
    },
    result: { requiresMFA: false, requiresHoldingSelection: false },
  }
}

interface AuthState {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  ssoAccountHint: SSOAccountHint | null
  // INC-031: Multi-holding support
  holdings: HoldingInfo[]
  activeHoldingId: string | null
  requiresHoldingSelection: boolean
  tempToken: string | null
  // AUTH_06b (#317): pase del 2FA (en memoria, no se guarda en el navegador) y su error
  mfaToken: string | null
  mfaError: MFAError | null
  mfaEnrollment: MFAEnrollment | null
  // SSO support
  ssoProviders: SSOProviderInfo[]
  traditionalLoginEnabled: boolean
  ssoLoading: string | null  // provider ID being loaded
  // AUTH_03 (#297): error del inicio del SSO, como codigo (la pantalla lo traduce)
  ssoError: SSOErrorCode | null
  // ALTA-014: Tenant Bootstrap
  tenantBootstrap: TenantBootstrap | null
  bootstrapLoading: boolean
  bootstrapError: string | null
  // AUTH_04 (#298): en memoria, como el pase del 2FA: la ruta publica desmonta la pantalla
  // mientras isLoading y una contrasena rechazada volvia al paso del correo
  loginDiscovery: LoginDiscovery | null
  // RBAC: Permissions, roles, and feature flags
  permissions: string[]
  roles: RoleInfo[]
  features: string[]
  // INC-077: Products available for current holding
  products: string[]

  // Actions
  login: (email: string, password: string, holdingId?: string) => Promise<LoginResult>
  completeMFALogin: (code: string) => Promise<LoginResult>
  // AUTH_06f (#321): alta obligatoria. QR → primer codigo (devuelve los codigos de
  // respaldo) → "Ya los guarde" abre la sesion
  startMFAEnrollment: () => Promise<{ secret: string; uri: string }>
  confirmMFAEnrollment: (code: string) => Promise<string[]>
  finishMFAEnrollment: () => LoginResult
  register: (email: string, password: string, fullName: string) => Promise<void>
  logout: () => void
  setLoginDiscovery: (discovery: LoginDiscovery | null) => void
  loadUser: () => Promise<void>
  loadPermissions: () => Promise<void>
  setError: (error: string | null) => void
  // INC-031: Holding selection actions
  selectHolding: (holdingId: string) => Promise<void>
  switchHolding: (holdingId: string) => Promise<void>
  // SSO actions
  loadSSOProviders: (holdingId?: string) => Promise<void>
  initiateSSOLogin: (providerId: string, holdingId?: string) => Promise<void>
  handleSSOCallback: (providerId: string, code: string, state: string) => Promise<LoginResult>
  // ALTA-014: Tenant Bootstrap actions
  loadTenantBootstrap: (overrideHost?: string) => Promise<TenantBootstrap | null>
  // ALTA-014: Handle SSO complete redirect from centralized callback
  handleSSOCompleteRedirect: () => boolean
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      ssoAccountHint: null,
      // INC-031: Multi-holding support
      holdings: [],
      activeHoldingId: null,
      requiresHoldingSelection: false,
      tempToken: null,
      mfaToken: null,
      mfaError: null,
      mfaEnrollment: null,
      // SSO support
      ssoProviders: [],
      traditionalLoginEnabled: true,
      ssoLoading: null,
      ssoError: null,
      // ALTA-014: Tenant Bootstrap
      tenantBootstrap: null,
      bootstrapLoading: false,
      bootstrapError: null,
      loginDiscovery: null,
      // RBAC: Permissions, roles, and feature flags
      permissions: [],
      roles: [],
      features: [],
      // INC-077: Products available for current holding
      products: [],

      login: async (email: string, password: string, holdingId?: string) => {
        set({ isLoading: true, error: null, ssoAccountHint: null, ssoError: null, mfaError: null, mfaToken: null, mfaEnrollment: null })
        try {
          // ALTA-014: Pass holdingId from tenant bootstrap to enforce auth policies
          const response = await authApi.login(email, password, holdingId)
          const data = response.data

          // AUTH_06b (#317): con 2FA el ingreso entrega un pase para /auth/mfa/login.
          // Va al store y no al estado de la pagina: la ruta publica la desmonta mientras isLoading.
          if (data.mfa_required) {
            // Sin restos de un ingreso anterior: la pantalla del codigo manda a la seleccion de
            // holding apenas ve requiresHoldingSelection
            set({ isLoading: false, mfaToken: data.mfa_token ?? null, requiresHoldingSelection: false, tempToken: null })
            return { requiresMFA: true, requiresHoldingSelection: false }
          }

          // AUTH_06f (#321): un holding exige 2FA y la cuenta no lo tiene. No hay sesion:
          // un pase de alta, en el store por lo mismo que el pase del codigo
          if (data.mfa_enrollment_required) {
            set({
              isLoading: false,
              mfaEnrollment: data.mfa_enrollment_token
                ? { token: data.mfa_enrollment_token, email: data.user?.email ?? email, pendingLogin: null }
                : null,
              requiresHoldingSelection: false,
              tempToken: null,
            })
            return { requiresMFA: false, requiresMFAEnrollment: true, requiresHoldingSelection: false }
          }

          const { state, result } = stateAfterLogin(data)
          set(state)
          return result
        } catch (error: unknown) {
          const ssoAccountHint = readSSOAccountHint(error)
          if (ssoAccountHint) {
            set({ ssoAccountHint, isLoading: false })
            throw error
          }
          // AUTH_06b (#317): cuenta bloqueada, 2FA sin configurar, sin regla, servicio caido
          const mfaError = readMFAError(error)
          if (mfaError) {
            set({ mfaError, isLoading: false })
            throw error
          }
          // AUTH_11 (#309): cuenta sin holding. Mismo codigo y mismo aviso traducido que el
          // retorno del SSO; antes se veia "Request failed with status code 403".
          // AUTH_04 (#298): tambien el holding solo-SSO y el holding sin metodo configurado
          // AUTH_24 (#359): y la cuenta desactivada; antes, un texto en ingles sin traducir
          const ssoCode = readSSOError(error)
          if (ssoCode !== 'unknown') {
            set({ ssoError: ssoCode, isLoading: false })
            throw error
          }
          const message = error instanceof Error ? error.message : 'Login failed'
          set({ error: message, isLoading: false })
          throw error
        }
      },

      // AUTH_06b (#317): segundo paso del ingreso con 2FA. No toca isLoading: la ruta
      // publica desmontaria la pantalla del codigo y se perderia lo escrito.
      completeMFALogin: async (code: string) => {
        const { mfaToken } = get()
        if (!mfaToken) {
          set({ mfaError: { code: 'mfa_token_invalid' } })
          throw new Error('No MFA pass available')
        }

        set({ mfaError: null, error: null })
        try {
          const response = await authApi.mfaLogin(mfaToken, code)
          const { state, result } = stateAfterLogin(response.data)
          set({ ...state, mfaToken: null })
          return result
        } catch (error: unknown) {
          const mfaError = readMFAError(error) ?? { code: 'unknown' as const }
          // Pase vencido, gastado o anulado, o cuenta bloqueada: ya no sirve, hay que
          // volver a la contrasena. Con un codigo malo o una falla pasajera sigue vivo.
          const passDead = mfaError.code === 'mfa_token_invalid' || mfaError.code === 'mfa_locked'
          set({ mfaError, mfaToken: passDead ? null : mfaToken })
          throw error
        }
      },

      // AUTH_06f (#321): los pasos del alta obligatoria tampoco tocan isLoading (la ruta
      // publica desmontaria la pantalla y se perderia el QR o los codigos)
      startMFAEnrollment: async () => {
        const { mfaEnrollment } = get()
        if (!mfaEnrollment) {
          set({ mfaError: { code: 'mfa_token_invalid' } })
          throw new Error('No MFA enrollment pass available')
        }
        set({ mfaError: null, error: null })
        try {
          const { data } = await authApi.mfaEnrollSetup(mfaEnrollment.token)
          return { secret: data.secret, uri: data.qr_code_uri }
        } catch (error: unknown) {
          set(stateAfterEnrollmentError(error, mfaEnrollment))
          throw error
        }
      },

      confirmMFAEnrollment: async (code: string) => {
        const { mfaEnrollment } = get()
        if (!mfaEnrollment) {
          set({ mfaError: { code: 'mfa_token_invalid' } })
          throw new Error('No MFA enrollment pass available')
        }
        set({ mfaError: null, error: null })
        try {
          const { data } = await authApi.mfaEnrollConfirm(mfaEnrollment.token, code)
          const { backup_codes: backupCodes, ...pendingLogin } = data as LoginResponseData & { backup_codes: string[] }
          // La sesion espera a que la persona guarde sus codigos: si no, la ruta publica
          // la mandaria a /home con los codigos sin mirar
          set({ mfaEnrollment: { ...mfaEnrollment, pendingLogin } })
          return backupCodes
        } catch (error: unknown) {
          set(stateAfterEnrollmentError(error, mfaEnrollment))
          throw error
        }
      },

      finishMFAEnrollment: () => {
        const pendingLogin = get().mfaEnrollment?.pendingLogin
        if (!pendingLogin) {
          throw new Error('MFA enrollment not confirmed')
        }
        const { state, result } = stateAfterLogin(pendingLogin)
        set({ ...state, mfaEnrollment: null, mfaError: null })
        return result
      },

      register: async (email: string, password: string, fullName: string) => {
        set({ isLoading: true, error: null })
        try {
          await authApi.register({ email, password, full_name: fullName })
          set({ isLoading: false })
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : 'Registration failed'
          set({ error: message, isLoading: false })
          throw error
        }
      },

      logout: () => {
        // AUTH_18 (#323): la cookie de la documentacion de la API solo la borra el servidor
        // (es HttpOnly). Si este pedido falla, la cookie vence con la sesion.
        authApi.logout().catch((error: unknown) => {
          console.warn('[auth] /auth/logout failed; the API docs cookie expires with the session', error)
        })
        clearPostLoginReturn()
        localStorage.removeItem('token')
        localStorage.removeItem('holdingId')
        localStorage.removeItem('empresaId')
        localStorage.removeItem('sso_state')
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
          // INC-031: Clear multi-holding state
          holdings: [],
          activeHoldingId: null,
          requiresHoldingSelection: false,
          tempToken: null,
          mfaToken: null,
          mfaError: null,
          mfaEnrollment: null,
          // AUTH_04 (#298): el correo de quien salio no queda en la pantalla de ingreso
          loginDiscovery: null,
          // Clear SSO state
          ssoLoading: null,
          // RBAC: Clear permissions state
          permissions: [],
          roles: [],
          features: [],
          // INC-077: Clear products
          products: [],
        })
      },

      setLoginDiscovery: (loginDiscovery) => set({ loginDiscovery }),

      loadUser: async () => {
        const token = localStorage.getItem('token')
        if (!token) {
          set({ isLoading: false })
          return
        }

        try {
          const response = await authApi.me()
          const mappedUser = mapUserFromBackend(response.data)

          // Also update localStorage with holding and empresa IDs
          if (mappedUser?.holding_id) {
            localStorage.setItem('holdingId', mappedUser.holding_id)
          }
          if (mappedUser?.empresa_id) {
            localStorage.setItem('empresaId', mappedUser.empresa_id)
          }

          set({
            user: mappedUser,
            token,
            isAuthenticated: true,
            isLoading: false,
          })

          // RBAC: Load permissions after user is loaded
          await get().loadPermissions()
        } catch {
          get().logout()
        }
      },

      // RBAC: Load user permissions, roles, feature flags, and products
      loadPermissions: async () => {
        try {
          // Load permissions
          const response = await authApi.myPermissions()
          const data = response.data

          // Map roles to RoleInfo format
          const roles: RoleInfo[] = (data.roles || []).map((r: { code: string; name: string; scope_type?: string }) => ({
            code: r.code,
            name: r.name,
            scope_type: r.scope_type || 'global',
          }))

          set({
            permissions: data.permissions || [],
            roles,
            features: data.features || [],
          })

          // INC-077: Load products for current holding
          try {
            const productsResponse = await authApi.myProducts()
            const productsData = productsResponse.data
            set({
              products: productsData.enabled_codes || [],
            })
          } catch (productsError) {
            console.error('Failed to load products:', productsError)
            // Don't fail - keep empty products
            set({ products: [] })
          }
        } catch (error) {
          console.error('Failed to load permissions:', error)
          // Don't fail silently - keep empty arrays
          set({
            permissions: [],
            roles: [],
            features: [],
            products: [],
          })
        }
      },

      setError: (error: string | null) => set({ error, ssoAccountHint: null, ssoError: null, mfaError: null }),

      // INC-031: Select holding after login (when requires_holding_selection=true)
      selectHolding: async (holdingId: string) => {
        const { tempToken, holdings } = get()
        if (!tempToken) {
          throw new Error('No temporary token available')
        }

        set({ isLoading: true, error: null })
        try {
          const response = await authApi.selectHolding(holdingId, tempToken)
          const data = response.data

          localStorage.setItem('token', data.access_token)
          localStorage.setItem('holdingId', holdingId)

          const mappedUser = mapUserFromBackend(data.user)
          const updatedHoldings = (data.holdings || holdings) as HoldingInfo[]

          set({
            user: mappedUser,
            token: data.access_token,
            isAuthenticated: true,
            isLoading: false,
            holdings: updatedHoldings,
            activeHoldingId: holdingId,
            requiresHoldingSelection: false,
            tempToken: null,
          })
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : 'Failed to select holding'
          set({ error: message, isLoading: false })
          throw error
        }
      },

      // INC-031: Switch to a different holding while logged in
      switchHolding: async (holdingId: string) => {
        set({ isLoading: true, error: null })
        try {
          const response = await authApi.switchHolding(holdingId)
          const data = response.data

          localStorage.setItem('token', data.access_token)
          localStorage.setItem('holdingId', holdingId)

          const mappedUser = mapUserFromBackend(data.user)
          const updatedHoldings = (data.holdings || get().holdings) as HoldingInfo[]

          set({
            user: mappedUser,
            token: data.access_token,
            isLoading: false,
            holdings: updatedHoldings,
            activeHoldingId: holdingId,
          })
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : 'Failed to switch holding'
          set({ error: message, isLoading: false })
          throw error
        }
      },

      // SSO: Load available providers for a holding
      loadSSOProviders: async (holdingId?: string) => {
        try {
          const response = await authApi.getSSOProviders(holdingId)
          const data = response.data
          set({
            ssoProviders: data.providers as SSOProviderInfo[],
            traditionalLoginEnabled: data.traditional_login_enabled,
          })
        } catch (error: unknown) {
          console.error('Failed to load SSO providers:', error)
          // Don't set error - fall back to traditional login
          set({
            ssoProviders: [],
            traditionalLoginEnabled: true,
          })
        }
      },

      // SSO: Initiate login with an SSO provider
      initiateSSOLogin: async (providerId: string, holdingId?: string) => {
        set({ ssoLoading: providerId, error: null, ssoError: null })
        try {
          const response = await authApi.initiateSSOLogin(providerId, holdingId)
          const { auth_url, state } = response.data

          // Store state for CSRF validation on callback
          localStorage.setItem('sso_state', state)
          if (holdingId) {
            localStorage.setItem('sso_holding_id', holdingId)
          }

          // Redirect to IdP
          window.location.href = auth_url
        } catch (error: unknown) {
          set({ ssoError: readSSOError(error), ssoLoading: null })
          throw error
        }
      },

      // SSO: Handle callback from IdP
      // ALTA-014: Supports centralized redirect - if redirect_host differs from current host,
      // redirects to original tenant with tokens passed via URL hash
      handleSSOCallback: (providerId: string, code: string, state: string) => {
        // AUTH_03 (#297): un solo envio por identificador (ver ssoCallbacks)
        const key = `${providerId}:${state}`
        const pending = ssoCallbacks.get(key)
        if (pending) return pending
        const run = (async (): Promise<LoginResult> => {
          set({ isLoading: true, error: null })
          try {
            // Validate state
            const storedState = localStorage.getItem('sso_state')
            if (storedState && storedState !== state) {
              throw new SSOError('sso_state_mismatch')
            }

            const response = await authApi.handleSSOCallback(providerId, code, state)
            const data = response.data

            // Clean up SSO state
            localStorage.removeItem('sso_state')
            localStorage.removeItem('sso_holding_id')

            // ALTA-014: Check if we need to redirect to original tenant subdomain
            const currentHost = window.location.host
            const redirectHost = data.redirect_host

            if (redirectHost && redirectHost !== currentHost && !data.mfa_required && !data.requires_holding_selection) {
              // Aikido HIGH: validate redirectHost against tenant pattern to prevent
              // open redirect / token-leak via malicious backend response (#581: lista arriba).
              if (!isAllowedSSORedirectHost(redirectHost)) {
                throw new SSOError('sso_redirect_host_invalid')
              }
              // Need to redirect to original tenant with tokens
              // Use URL hash to pass tokens securely (hash is not sent to server)
              const ssoResult = {
                access_token: data.access_token,
                refresh_token: data.refresh_token,
                user: data.user,
                holdings: data.holdings || [],
              }
              const encodedResult = btoa(JSON.stringify(ssoResult))
              // Redirect to original tenant with SSO result in hash
              window.location.href = `https://${redirectHost}/#sso_complete=${encodedResult}`
              // Return early - the redirect will navigate away
              return { requiresMFA: false, requiresHoldingSelection: false }
            }

            // Handle MFA requirement (if IdP user has MFA enabled)
            // AUTH_06b (#317): mismo pase que el ingreso con contrasena
            if (data.mfa_required) {
              set({ isLoading: false, mfaToken: data.mfa_token ?? null, mfaError: null })
              return { requiresMFA: true, requiresHoldingSelection: false }
            }

            // Handle multi-holding selection
            const holdings = (data.holdings || []) as HoldingInfo[]

            if (data.requires_holding_selection) {
              set({
                isLoading: false,
                holdings,
                requiresHoldingSelection: true,
                tempToken: data.temp_token,
              })
              return { requiresMFA: false, requiresHoldingSelection: true, tempToken: data.temp_token, holdings }
            }

            // Normal login completion (same host or no redirect needed)
            localStorage.setItem('token', data.access_token)
            const activeHoldingId = holdings.length > 0 ? holdings[0].id : null
            if (activeHoldingId) {
              localStorage.setItem('holdingId', activeHoldingId)
            }
            if (data.user?.empresa_id) {
              localStorage.setItem('empresaId', data.user.empresa_id)
            }

            const mappedUser = mapUserFromBackend(data.user)

            set({
              user: mappedUser,
              token: data.access_token,
              isAuthenticated: true,
              isLoading: false,
              holdings,
              activeHoldingId,
              requiresHoldingSelection: false,
              tempToken: null,
            })

            return { requiresMFA: false, requiresHoldingSelection: false }
          } catch (error: unknown) {
            // Clean up SSO state on error
            localStorage.removeItem('sso_state')
            localStorage.removeItem('sso_holding_id')
            // El mensaje lo elige la pagina segun el codigo (readSSOError)
            set({ isLoading: false })
            throw error
          }
        })()
        ssoCallbacks.set(key, run)
        return run
      },

      // ALTA-014: Load tenant bootstrap from hostname
      loadTenantBootstrap: async (overrideHost?: string) => {
        set({ bootstrapLoading: true, bootstrapError: null })
        try {
          // ALTA-014: Always pass current hostname so backend can resolve tenant
          const hostToUse = overrideHost || window.location.host
          const response = await authApi.tenantBootstrap(hostToUse)
          const data = response.data

          // Check for bootstrap error
          if (data.error) {
            set({
              bootstrapLoading: false,
              bootstrapError: data.error,
              tenantBootstrap: data,
              // If there's an error but authPolicy exists, use it
              traditionalLoginEnabled: data.authPolicy?.allowPasswordLogin ?? true,
            })
            return data
          }

          // Convert SSO providers from bootstrap to store format
          const ssoProviders = ssoProvidersFromBootstrap(data)

          set({
            bootstrapLoading: false,
            bootstrapError: null,
            tenantBootstrap: data,
            // Update SSO state from bootstrap
            ssoProviders,
            traditionalLoginEnabled: data.authPolicy.allowPasswordLogin,
          })

          return data
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : 'Failed to load tenant configuration'
          set({
            bootstrapLoading: false,
            bootstrapError: message,
            tenantBootstrap: null,
            // Fallback to traditional login on error
            traditionalLoginEnabled: true,
            ssoProviders: [],
          })
          return null
        }
      },

      // ALTA-014: Handle SSO complete redirect from centralized callback
      // Called on app initialization to check for sso_complete hash
      handleSSOCompleteRedirect: () => {
        const hash = window.location.hash
        if (!hash.includes('sso_complete=')) {
          return false
        }

        try {
          // Extract and decode the SSO result
          const match = hash.match(/sso_complete=([^&]+)/)
          if (!match) {
            return false
          }

          const encodedResult = match[1]
          const ssoResult = JSON.parse(atob(encodedResult))

          // Store tokens
          localStorage.setItem('token', ssoResult.access_token)
          const holdings = (ssoResult.holdings || []) as HoldingInfo[]
          const activeHoldingId = holdings.length > 0 ? holdings[0].id : null
          if (activeHoldingId) {
            localStorage.setItem('holdingId', activeHoldingId)
          }
          if (ssoResult.user?.empresa_id) {
            localStorage.setItem('empresaId', ssoResult.user.empresa_id)
          }

          // Map user
          const mappedUser = mapUserFromBackend(ssoResult.user)

          // Update store
          set({
            user: mappedUser,
            token: ssoResult.access_token,
            isAuthenticated: true,
            isLoading: false,
            holdings,
            activeHoldingId,
            requiresHoldingSelection: false,
            tempToken: null,
          })

          // Clear the hash from URL (without triggering a reload)
          window.history.replaceState(null, '', window.location.pathname + window.location.search)

          return true
        } catch (error) {
          console.error('Failed to process SSO complete redirect:', error)
          // Clear invalid hash
          window.history.replaceState(null, '', window.location.pathname + window.location.search)
          return false
        }
      },
    }),
    {
      name: 'auth-storage',
      // Persist token and user for session restoration
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        // INC-031: Persist holdings state
        holdings: state.holdings,
        activeHoldingId: state.activeHoldingId,
        // RBAC: Persist permissions state
        permissions: state.permissions,
        roles: state.roles,
        features: state.features,
        // INC-077: Persist products
        products: state.products,
      }),
    }
  )
)
